import { mxn } from "@/lib/format";
import { fechaCorta } from "@/lib/migracion";

const TONO = { ERROR: "text-red-700", OMITIDA: "text-slate-500", ADVERTENCIA: "text-amber-700" };
const tono = (txt) => TONO[String(txt || "").split(":")[0]] || "text-emerald-700";
const dinero = (v) => (v == null ? "—" : mxn(v));
const cifra = (v) => (v == null ? "—" : v);

const AntesDespues = ({ antes, despues, fmt, testid }) => (
  <span className="whitespace-nowrap font-mono tabular-nums" data-testid={testid}>
    <span className="text-slate-400">{fmt(antes)}</span> <span className="text-slate-300">→</span>{" "}
    <span className="text-slate-900">{fmt(despues)}</span>
  </span>
);

const PagosReales = ({ pagos, fila }) => {
  if (!pagos?.length) return <span className="text-slate-400">Sin pagos reales</span>;
  return (
    <ul className="space-y-0.5" data-testid={`migracion-correccion-${fila}-pagos`}>
      {pagos.map((p, i) => (
        <li key={`${p.recibo}-${i}`} className="whitespace-nowrap font-mono text-[11px] text-slate-600">
          {p.recibo} · {dinero(p.monto)} · {fechaCorta(p.fecha_pago)} → cuota {(p.cuotas || p.cuotas_afectadas || []).join(", ") || "—"}
          {p.gastos_cobranza > 0 && <span className="text-amber-700"> · gastos de cobranza {dinero(p.gastos_cobranza)}</span>}
          {p.saldo_a_favor > 0 && <span className="text-amber-700"> · saldo a favor {dinero(p.saldo_a_favor)}</span>}
        </li>
      ))}
    </ul>
  );
};

const Fila = ({ f }) => {
  const pv = f.previa || {};
  const n = f.real || f.previa || {};
  const a = n.antes || {};
  const td = "px-3 py-2 align-top";
  const txt = (v) => v || "—";
  return (
    <tr data-testid={`migracion-correccion-${f.fila}`}>
      <td className={`${td} font-mono text-xs text-slate-500`}>{f.fila}</td>
      <td className={`${td} whitespace-nowrap font-mono text-xs text-slate-800`}>{pv.numero_credito || f.nombre || "—"}</td>
      <td className={`${td} text-slate-700`}>{pv.cliente || "—"}</td>
      <td className={td}><AntesDespues antes={pv.producto_antes ?? pv.producto} despues={pv.producto_despues ?? pv.producto} fmt={txt} testid={`migracion-correccion-${f.fila}-producto`} /></td>
      <td className={td}><AntesDespues antes={pv.periodicidad_antes} despues={pv.periodicidad_despues} fmt={txt} testid={`migracion-correccion-${f.fila}-periodicidad`} /></td>
      <td className={td}><AntesDespues antes={pv.fecha_actual} despues={pv.fecha_original} fmt={fechaCorta} testid={`migracion-correccion-${f.fila}-fecha`} /></td>
      <td className={td}><AntesDespues antes={pv.tasa_antes} despues={pv.tasa_despues} fmt={(v) => (v == null ? "—" : `${v}%`)} testid={`migracion-correccion-${f.fila}-tasa`} /></td>
      <td className={td}><AntesDespues antes={pv.plazo_antes ?? pv.plazo} despues={pv.plazo_despues ?? pv.plazo} fmt={cifra} testid={`migracion-correccion-${f.fila}-plazo`} /></td>
      <td className={`${td} text-right font-mono tabular-nums`}>{cifra(pv.cuotas_antes_felvora)}</td>
      <td className={td}><AntesDespues antes={a.saldo_insoluto} despues={n.saldo_insoluto} fmt={dinero} testid={`migracion-correccion-${f.fila}-saldo`} /></td>
      <td className={td}><AntesDespues antes={a.cuotas_vencidas} despues={n.cuotas_vencidas} fmt={cifra} testid={`migracion-correccion-${f.fila}-vencidas`} /></td>
      <td className={td}><AntesDespues antes={a.interes_moratorio} despues={n.interes_moratorio} fmt={dinero} testid={`migracion-correccion-${f.fila}-mora`} /></td>
      <td className={td}><PagosReales pagos={n.pagos_reales} fila={f.fila} /></td>
      <td className={`${td} text-xs ${tono(f.resultado_texto)}`} data-testid={`migracion-correccion-${f.fila}-resultado`}>
        {f.resultado_texto}
        {f.real && <span className="ml-1 text-slate-400">· cifras reales</span>}
      </td>
    </tr>
  );
};

// Hoja «Corregir creditos»: condiciones antes → después y los pagos reales que se reaplican.
export function TablaCorrecciones({ filas }) {
  if (!filas.length) return null;
  const th = "px-3 py-2";
  return (
    <div data-testid="migracion-tabla-correcciones">
      <p className="mb-2 text-sm font-semibold text-slate-800">Corregir créditos</p>
      <div className="max-h-96 overflow-auto rounded-md border border-slate-200">
        <table className="w-full min-w-[1900px] text-sm">
          <thead className="sticky top-0 bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className={th}>Fila</th><th className={`${th} whitespace-nowrap`}>Crédito</th><th className={`${th} min-w-[150px]`}>Cliente</th>
              <th className={th}>Producto</th><th className={th}>Periodicidad</th>
              <th className={th}>Fecha de disposición</th><th className={th}>Tasa</th><th className={th}>Plazo</th>
              <th className={`${th} text-right`}>Cuotas antes de FELVORA</th><th className={th}>Saldo</th>
              <th className={th}>Cuotas vencidas</th><th className={th}>Mora</th>
              <th className={th}>Pagos reales que se reaplican</th><th className={`${th} min-w-[420px]`}>Resultado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filas.map((f) => <Fila key={`k-${f.fila}`} f={f} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}
